// src/pages/UserProfilePage.tsx
// RONYX THEME — teal/ink/gold
// ALL LOGIC, HANDLERS, SERVICE CALLS UNTOUCHED
// Added: banner display, interests chips, email (own profile only), full_name display
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import {
  getUserProfile,
  getUserPosts,
  checkIfFollowing,
  followUser,
  unfollowUser,
  UserProfile,
  UserPost,
} from '../services/userProfileService';
import {
  FaArrowLeft, FaCalendarAlt, FaHeart, FaComment, FaShare,
  FaMapMarkerAlt, FaLink, FaEnvelope, FaVideo,
  FaFire, FaBook, FaGamepad, FaPalette, FaMusic,
  FaStar, FaCode, FaUserPlus, FaUserCheck, FaSpinner,
  FaUserCircle, FaImage,
} from 'react-icons/fa';

// ── INTERESTS MAP (mirrors ProfilePage + ProfileSetup) ───────────
const AVAILABLE_INTERESTS = [
  { id: 'anime',     label: 'Anime',       icon: FaFire,    color: '#e8a820' },
  { id: 'manga',     label: 'Manga',       icon: FaBook,    color: '#a855f7' },
  { id: 'gaming',    label: 'Gaming',      icon: FaGamepad, color: '#3b82f6' },
  { id: 'art',       label: 'Digital Art', icon: FaPalette, color: '#10b981' },
  { id: 'music',     label: 'J-Pop/K-Pop', icon: FaMusic,   color: '#ec4899' },
  { id: 'cosplay',   label: 'Cosplay',     icon: FaStar,    color: '#f59e0b' },
  { id: 'coding',    label: 'Coding',      icon: FaCode,    color: '#6366f1' },
  { id: 'streaming', label: 'Streaming',   icon: FaVideo,   color: '#8b5cf6' },
];

// ── LEVEL RANKS ──────────────────────────────────────────────────
const LEVEL_RANKS = [
  { min: 1,   max: 9,   title: 'Rookie',      color: '#888780' },
  { min: 10,  max: 24,  title: 'Rising Star',  color: '#5ab8b8' },
  { min: 25,  max: 49,  title: 'Elite',        color: '#a855f7' },
  { min: 50,  max: 99,  title: 'Master',       color: '#e8a820' },
  { min: 100, max: 999, title: 'Legend',       color: '#e05050' },
];

const getRank = (lvl: number) =>
  LEVEL_RANKS.find(r => lvl >= r.min && lvl <= r.max) || LEVEL_RANKS[0];

// ── COMPONENT ────────────────────────────────────────────────────
export default function UserProfilePage() {
  const { username } = useParams<{ username: string }>();
  const navigate     = useNavigate();
  const { user }     = useAuth();

  // ── STATE (untouched) ────────────────────────────────────────
  const [profile, setProfile]           = useState<UserProfile | null>(null);
  const [posts, setPosts]               = useState<UserPost[]>([]);
  const [isFollowing, setIsFollowing]   = useState(false);
  const [loading, setLoading]           = useState(true);
  const [followLoading, setFollowLoading] = useState(false);

  const isOwnProfile = user && profile && user.id === profile.id;

  // ── EFFECTS (untouched) ──────────────────────────────────────
  useEffect(() => {
    if (!username) return;
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  // ── HANDLERS (all untouched) ─────────────────────────────────
  const loadProfile = async () => {
    if (!username) return;
    setLoading(true);
    try {
      const profileData = await getUserProfile(username);
      if (!profileData) {
        toast.error('User not found');
        navigate('/home');
        return;
      }
      setProfile(profileData);
      const postsData = await getUserPosts(profileData.id);
      setPosts(postsData);
      if (user && user.id !== profileData.id) {
        const following = await checkIfFollowing(user.id, profileData.id);
        setIsFollowing(following);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!user || !profile) return;
    setFollowLoading(true);
    try {
      if (isFollowing) {
        const success = await unfollowUser(user.id, profile.id);
        if (success) {
          setIsFollowing(false);
          setProfile({ ...profile, follower_count: profile.follower_count - 1 });
          toast.success(`Unfollowed ${profile.username}`);
        }
      } else {
        const success = await followUser(user.id, profile.id);
        if (success) {
          setIsFollowing(true);
          setProfile({ ...profile, follower_count: profile.follower_count + 1 });
          toast.success(`Following ${profile.username}`);
        }
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const formatCount = (n: number) =>
    n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : String(n);

  // ── STYLE TOKENS ─────────────────────────────────────────────
  const ink      = '#f0fafa';
  const inkMid   = 'rgba(240,250,250,0.6)';
  const inkFaint = 'rgba(90,184,184,0.2)';
  const cardBg   = '#0f2424';
  const pageBg   = '#0d1a1a';
  const bc       = 'rgba(90,184,184,0.12)';
  const gold     = '#e8a820';
  const teal     = '#7ecece';
  const tealMid  = '#5ab8b8';

  // ── LOADING ──────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(90,184,184,0.2)', borderTop: `3px solid ${teal}`, animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
        <p style={{ color: tealMid, fontSize: '11px', fontWeight: 900, letterSpacing: '2px', fontFamily: "'M PLUS Rounded 1c', sans-serif" }}>
          LOADING PROFILE...
        </p>
      </div>
    </div>
  );

  if (!profile) return (
    <div style={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <FaUserCircle style={{ fontSize: 56, color: inkFaint, marginBottom: 14 }} />
        <p style={{ color: ink, fontWeight: 900, fontSize: 18, fontFamily: "'M PLUS Rounded 1c', sans-serif" }}>User not found</p>
        <p style={{ color: inkMid, fontSize: 13, marginTop: 6, fontFamily: "'M PLUS Rounded 1c', sans-serif" }}>This profile doesn't exist</p>
      </div>
    </div>
  );

  const rank          = getRank(((profile as any).level as number) || 1);
  const userInterests = (profile as any).interests as string[] | null;
  const bannerUrl     = (profile as any).banner_url as string | null;
  const email         = (profile as any).email as string | null;
  const mediaPosts    = posts.filter(p => p.media_url);

  return (
    <div style={{ minHeight: '100vh', background: pageBg, fontFamily: "'M PLUS Rounded 1c', sans-serif", paddingBottom: 60 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=M+PLUS+Rounded+1c:wght@400;700;900&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }

        .rup-grid-cell { position: relative; overflow: hidden; cursor: pointer; }
        .rup-grid-cell:hover .rup-hover { opacity: 1 !important; }
        .rup-interest:hover { opacity: 0.85; }
        .rup-gold:hover:not(:disabled) { transform: translate(1px,1px) !important; box-shadow: 1px 1px 0 #0d1a1a !important; }
        .rup-ghost:hover { border-color: rgba(90,184,184,0.35) !important; color: ${teal} !important; }
        .rup-post-card:hover { border-color: rgba(90,184,184,0.3) !important; }

        /* Banner height */
        .rup-banner { height: 160px; }
        @media (min-width: 640px) { .rup-banner { height: 220px; } }

        /* Grid: 1 col mobile → 2 → 3 */
        .rup-posts-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
        @media (min-width: 480px) { .rup-posts-grid { grid-template-columns: repeat(2,1fr); } }
        @media (min-width: 768px) { .rup-posts-grid { grid-template-columns: repeat(3,1fr); } }

        /* Profile layout: stacked mobile → side-by-side desktop */
        .rup-profile-layout { display: flex; flex-direction: column; gap: 16px; }
        @media (min-width: 768px) { .rup-profile-layout { flex-direction: row; align-items: flex-start; gap: 20px; } }

        .rup-sidebar { width: 100%; }
        @media (min-width: 768px) { .rup-sidebar { width: 260px; flex-shrink: 0; } }
      `}</style>

      {/* ── STICKY TOP NAV ───────────────────────────────────── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 40,
        background: 'rgba(13,26,26,0.95)', backdropFilter: 'blur(12px)',
        borderBottom: `1.5px solid ${bc}`,
        padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(90,184,184,0.08)',
            border: `1.5px solid ${bc}`, borderRadius: '8px',
            color: teal, padding: '7px 10px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
          }}
        >
          <FaArrowLeft style={{ fontSize: 13 }} />
        </button>
        <div>
          <h1 style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 18, letterSpacing: '2px', color: ink, lineHeight: 1,
          }}>
            {profile.username}
          </h1>
          <p style={{ fontSize: 10, fontWeight: 700, color: inkMid, letterSpacing: '1px', marginTop: 1 }}>
            {posts.length} POST{posts.length !== 1 ? 'S' : ''}
          </p>
        </div>
      </nav>

      {/* ── BANNER ───────────────────────────────────────────── */}
      <div className="rup-banner" style={{ position: 'relative', overflow: 'hidden' }}>
        {bannerUrl ? (
          <img src={bannerUrl} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{
            width: '100%', height: '100%', position: 'relative',
            background: 'linear-gradient(135deg, #0d1a1a 0%, #1a2e2e 40%, #0f2424 100%)',
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: `linear-gradient(rgba(90,184,184,0.07) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(90,184,184,0.07) 1px, transparent 1px)`,
              backgroundSize: '28px 28px',
            }} />
            {/* Shogi piece watermark */}
            <svg style={{ position: 'absolute', right: 20, bottom: 8, opacity: 0.07, pointerEvents: 'none' }}
              width="72" height="80" viewBox="0 0 100 110">
              <path d="M50 8 L82 22 L88 88 Q88 96 80 96 L20 96 Q12 96 12 88 L18 22 Z" fill={teal} />
            </svg>
            <div style={{
              position: 'absolute', bottom: 12, left: 16,
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 'clamp(24px, 6vw, 44px)', letterSpacing: '8px', lineHeight: 1,
              color: 'rgba(126,206,206,0.12)', userSelect: 'none',
            }}>RONYX NETWORK</div>
          </div>
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 35%, rgba(13,26,26,0.7) 100%)' }} />
      </div>

      {/* ── CONTENT ──────────────────────────────────────────── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 12px' }}>
        <div className="rup-profile-layout" style={{ paddingTop: 0 }}>

          {/* ── SIDEBAR ─────────────────────────────────────── */}
          <aside className="rup-sidebar">

            {/* Identity card — avatar overlaps banner */}
            <div style={{
              background: cardBg, border: `1.5px solid ${bc}`, borderRadius: '14px',
              padding: '0 18px 18px', marginTop: '-36px', position: 'relative',
            }}>
              {/* Avatar row */}
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', paddingTop: 8 }}>
                <div style={{ position: 'relative' }}>
                  {profile.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={profile.username}
                      style={{ width: 76, height: 76, borderRadius: '12px', objectFit: 'cover', border: `3px solid ${cardBg}`, boxShadow: '0 4px 16px rgba(13,26,26,0.4)' }}
                    />
                  ) : (
                    <div style={{
                      width: 76, height: 76, borderRadius: '12px',
                      border: `3px solid ${cardBg}`,
                      background: '#0d1a1a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(13,26,26,0.4)',
                    }}>
                      <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: teal, letterSpacing: '1px' }}>
                        {profile.username[0].toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Follow / Edit button */}
                {!isOwnProfile ? (
                  <button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className="rup-gold"
                    style={{
                      background: isFollowing ? 'rgba(90,184,184,0.1)' : gold,
                      border: `2.5px solid ${isFollowing ? 'rgba(90,184,184,0.3)' : '#0d1a1a'}`,
                      borderRadius: '8px',
                      boxShadow: isFollowing ? 'none' : '3px 3px 0 #0d1a1a, 4px 4px 0 #8a5a00',
                      color: isFollowing ? teal : '#0d1a1a',
                      fontFamily: "'M PLUS Rounded 1c', sans-serif",
                      fontSize: '11px', fontWeight: 900, letterSpacing: '1px',
                      padding: '9px 16px', cursor: followLoading ? 'not-allowed' : 'pointer',
                      opacity: followLoading ? 0.55 : 1,
                      display: 'flex', alignItems: 'center', gap: 6,
                      transition: 'transform 0.1s, box-shadow 0.1s',
                    }}
                  >
                    {followLoading
                      ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite', fontSize: 11 }} />
                      : isFollowing ? <FaUserCheck style={{ fontSize: 11 }} /> : <FaUserPlus style={{ fontSize: 11 }} />
                    }
                    {followLoading ? 'LOADING...' : isFollowing ? 'FOLLOWING' : 'FOLLOW'}
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/profile')}
                    className="rup-ghost"
                    style={{
                      background: 'transparent',
                      border: `1.5px solid ${bc}`, borderRadius: '8px',
                      color: inkMid, fontFamily: "'M PLUS Rounded 1c', sans-serif",
                      fontSize: '11px', fontWeight: 900, letterSpacing: '1px',
                      padding: '9px 16px', cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    EDIT PROFILE
                  </button>
                )}
              </div>

              {/* Name + username + rank */}
              <div style={{ marginTop: 14 }}>
                {profile.full_name && (
                  <p style={{ fontSize: 11, fontWeight: 700, color: inkMid, marginBottom: 2, letterSpacing: '0.3px' }}>
                    {profile.full_name}
                  </p>
                )}
                <h2 style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 'clamp(20px, 5vw, 26px)',
                  letterSpacing: '2px', color: ink, margin: 0, lineHeight: 1,
                }}>
                  {profile.username}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
                  <span style={{ fontSize: 10, fontWeight: 900, color: rank.color, letterSpacing: '1px' }}>
                    {rank.title.toUpperCase()}
                  </span>
                  <span style={{ color: inkFaint, fontSize: 9 }}>·</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: tealMid }}>
                    LVL 1
                  </span>
                </div>
              </div>

              {/* Stats row */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3,1fr)',
                marginTop: 16, borderTop: `1px solid ${bc}`, paddingTop: 12, gap: 4,
              }}>
                {[
                  { label: 'POSTS',     value: formatCount(posts.length),                 action: undefined },
                  { label: 'FOLLOWERS', value: formatCount(profile.follower_count || 0),  action: undefined },
                  { label: 'FOLLOWING', value: formatCount(profile.following_count || 0), action: undefined },
                ].map(s => (
                  <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <span style={{ fontSize: 16, fontWeight: 900, color: ink, lineHeight: 1 }}>{s.value}</span>
                    <span style={{ fontSize: 9, fontWeight: 900, color: inkMid, letterSpacing: '0.5px' }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── ABOUT card ── */}
            <div style={{ background: cardBg, border: `1.5px solid ${bc}`, borderRadius: '14px', padding: 18, marginTop: 10 }}>
              <p style={{ fontSize: 10, fontWeight: 900, color: inkMid, letterSpacing: '2px', marginBottom: 12 }}>ABOUT</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                {/* Bio */}
                {profile.bio && (
                  <p style={{ fontSize: 13, fontWeight: 700, color: ink, lineHeight: 1.7, margin: 0 }}>
                    {profile.bio}
                  </p>
                )}

                {/* Email — own profile only */}
                {isOwnProfile && email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaEnvelope style={{ color: tealMid, fontSize: 11, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: inkMid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {email}
                    </span>
                  </div>
                )}

                {/* Location */}
                {(profile as any).location && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaMapMarkerAlt style={{ color: tealMid, fontSize: 11, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: inkMid }}>{(profile as any).location}</span>
                  </div>
                )}

                {/* Website */}
                {(profile as any).website && (
                  <a href={(profile as any).website} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: tealMid, textDecoration: 'none' }}>
                    <FaLink style={{ fontSize: 11, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(profile as any).website.replace(/^https?:\/\//, '')}
                    </span>
                  </a>
                )}

                {/* Joined */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaCalendarAlt style={{ color: tealMid, fontSize: 11, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: inkMid }}>Joined {formatDate(profile.created_at)}</span>
                </div>

                {!profile.bio && !(profile as any).location && !(profile as any).website && (
                  <p style={{ fontSize: 12, color: inkMid }}>No info added yet.</p>
                )}
              </div>
            </div>

            {/* ── INTERESTS card ── */}
            {userInterests && userInterests.length > 0 && (
              <div style={{ background: cardBg, border: `1.5px solid ${bc}`, borderRadius: '14px', padding: 18, marginTop: 10 }}>
                <p style={{ fontSize: 10, fontWeight: 900, color: inkMid, letterSpacing: '2px', marginBottom: 14 }}>INTERESTS</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {userInterests.map(id => {
                    const int = AVAILABLE_INTERESTS.find(i => i.id === id);
                    if (!int) return null;
                    const Icon = int.icon;
                    return (
                      <span key={id} className="rup-interest" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '6px 12px', borderRadius: '20px',
                        background: '#0d1a1a', border: `1.5px solid ${bc}`,
                        fontSize: 11, fontWeight: 900, letterSpacing: '0.5px',
                        color: int.color, cursor: 'default',
                      }}>
                        <Icon style={{ fontSize: 10 }} />
                        {int.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── FAVOURITE ANIME card ── */}
            {(profile as any).favorite_anime && ((profile as any).favorite_anime as string[]).length > 0 && (
              <div style={{ background: cardBg, border: `1.5px solid ${bc}`, borderRadius: '14px', padding: 18, marginTop: 10 }}>
                <p style={{ fontSize: 10, fontWeight: 900, color: inkMid, letterSpacing: '2px', marginBottom: 12 }}>FAVOURITE ANIME</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {((profile as any).favorite_anime as string[]).map((anime: string, i: number) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: '8px',
                      background: '#0d1a1a', border: `1.5px solid ${bc}`,
                    }}>
                      <span style={{ fontSize: 10, fontWeight: 900, color: gold, minWidth: 16 }}>{i + 1}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: ink }}>{anime}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>

          {/* ── POSTS AREA ──────────────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Section header */}
            <div style={{
              background: cardBg, border: `1.5px solid ${bc}`, borderRadius: '14px',
              padding: '12px 16px', marginBottom: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: '3px', color: ink, margin: 0 }}>
                POSTS
              </p>
              <span style={{ fontSize: 10, fontWeight: 900, color: inkMid, letterSpacing: '1px' }}>
                {posts.length} TOTAL · {mediaPosts.length} MEDIA
              </span>
            </div>

            {/* Empty */}
            {posts.length === 0 ? (
              <div style={{
                background: cardBg, border: `1.5px solid ${bc}`, borderRadius: '14px',
                padding: '48px 24px', textAlign: 'center',
              }}>
                <FaImage style={{ fontSize: 40, color: 'rgba(90,184,184,0.15)', marginBottom: 12 }} />
                <p style={{ fontSize: 14, fontWeight: 900, color: ink, letterSpacing: '0.5px', marginBottom: 6 }}>No posts yet</p>
                {isOwnProfile && (
                  <p style={{ fontSize: 12, color: inkMid }}>Share your first post from the feed.</p>
                )}
              </div>
            ) : (
              <div className="rup-posts-grid">
                {posts.map(post => (
                  <div
                    key={post.id}
                    className="rup-post-card"
                    onClick={() => navigate('/home')}
                    style={{
                      background: cardBg,
                      border: `1.5px solid ${bc}`,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                  >
                    {/* Media */}
                    {post.media_url && (
                      <div style={{ aspectRatio: '1', overflow: 'hidden', position: 'relative' }}>
                        {post.media_type === 'video' ? (
                          <video src={post.media_url} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        ) : (
                          <img src={post.media_url} alt="Post" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        )}
                        {post.media_type === 'video' && (
                          <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(13,26,26,0.8)', borderRadius: '4px', padding: '3px 6px' }}>
                            <FaVideo style={{ color: teal, fontSize: 9 }} />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Content preview */}
                    <div style={{ padding: '12px 14px' }}>
                      {post.content && (
                        <p style={{
                          fontSize: 12, fontWeight: 700, color: ink, lineHeight: 1.6,
                          marginBottom: 10,
                          overflow: 'hidden', display: '-webkit-box',
                          WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                        }}>
                          {post.content}
                        </p>
                      )}

                      {/* Post stats */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        {[
                          { icon: <FaHeart style={{ color: gold, fontSize: 11 }} />,    count: post.likes },
                          { icon: <FaComment style={{ color: tealMid, fontSize: 11 }} />, count: post.comments_count },
                          { icon: <FaShare style={{ color: inkMid, fontSize: 11 }} />,    count: post.shares },
                        ].map((s, i) => (
                          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 900, color: inkMid }}>
                            {s.icon}{formatCount(s.count)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}